import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FileText, Download, ChevronRight, FlaskConical, Users, Calendar as CalIcon } from 'lucide-react';
import { usePageContent } from '../hooks/usePageContent';
import { usePageTitle } from '../hooks/usePageTitle';
import { useT } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  'All',
  'Chakra Energy',
  'Mind Harmonization',
  'Dosha Cleansing',
  'Vibrational Science',
];

export const Publications = () => {
  usePageTitle('Publications');
  const { t } = usePageContent('publications');
  const tr = useT();
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/publications`)
      .then(({ data }) => setPublications(data))
      .catch(() => setPublications([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (filter === 'All') return publications;
    return publications.filter((p) => p.category === filter);
  }, [publications, filter]);

  const caseStudies = publications.filter((p) => p.is_featured);

  return (
    <div className="min-h-screen pt-20" data-testid="publications-page">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-cream via-white to-saffron-50">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="text-saffron-600 uppercase text-xs md:text-sm tracking-[0.25em] font-body font-semibold mb-4">
              {t('hero_overline', 'Spiritual Research & Empirical Studies')}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-charcoal-800 tracking-tighter leading-none mb-6">
              {t('hero_title', 'Bridging Ancient Wisdom with Modern Inquiry')}
            </h1>
            <p className="text-lg md:text-xl text-charcoal-600 font-body leading-relaxed max-w-3xl mx-auto">
              {t(
                'hero_description',
                'A living archive of research, empirical studies, and scriptural commentary produced under the guidance of Guruji Risheeshwar Narendranji — where Vedic insight meets modern methodology.',
              )}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Methodology */}
      <section className="py-20 md:py-28 bg-white" data-testid="publications-methodology">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <div className="text-saffron-600 uppercase text-xs tracking-[0.2em] font-body font-semibold mb-3">
              {t('method_overline', 'Our Methodology')}
            </div>
            <h2 className="text-3xl md:text-5xl font-heading tracking-tight text-charcoal-800 mb-5">
              {t('method_heading', 'Research Guided by Risheeshwar Narendranji')}
            </h2>
            <p className="text-base md:text-lg text-charcoal-600 max-w-3xl mx-auto leading-relaxed">
              {t(
                'method_description',
                'Every study begins with scriptural insight, is refined through direct sadhana, and validated through observation, empirical measurement, and community experience.',
              )}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { titleKey: 'method_1_title', titleDefault: 'Scriptural Foundation', descKey: 'method_1_desc', descDefault: 'Every inquiry begins with a rigorous reading of the Vedas, Upanishads, and allied texts.' },
              { titleKey: 'method_2_title', titleDefault: 'Empirical Sadhana', descKey: 'method_2_desc', descDefault: 'Practitioners embody the teaching through Mantra, Dhyana, and Agni Kriyas, recording direct experience.' },
              { titleKey: 'method_3_title', titleDefault: 'Modern Verification', descKey: 'method_3_desc', descDefault: 'Findings are corroborated with contemporary science, phenomenology, and case documentation.' },
            ].map((m, i) => (
              <div key={i} className="bg-cream rounded-2xl p-7 border border-charcoal-100">
                <div className="w-11 h-11 rounded-full bg-saffron-100 flex items-center justify-center mb-4 text-saffron-700">
                  <FlaskConical size={20} />
                </div>
                <h3 className="text-xl font-heading text-charcoal-800 mb-2">{t(m.titleKey, m.titleDefault)}</h3>
                <p className="text-sm text-charcoal-600 leading-relaxed">{t(m.descKey, m.descDefault)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Filter + Grid */}
      <section className="py-20 md:py-28 bg-cream" data-testid="publications-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-10 text-center">
            <h2 className="text-3xl md:text-4xl font-heading text-charcoal-800 mb-6">
              {t('archive_heading', 'Research Archive')}
            </h2>
            <div className="flex flex-wrap gap-2 justify-center">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  data-testid={`publications-filter-${c.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors border ${
                    filter === c
                      ? 'bg-saffron-600 text-white border-saffron-600'
                      : 'bg-white text-charcoal-700 border-charcoal-200 hover:border-saffron-400'
                  }`}
                >
                  {c === 'All' ? tr('All') : tr(c)}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="text-center text-charcoal-500 py-12">{tr('Loading publications…')}</div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-charcoal-500 py-16 bg-white rounded-2xl border border-charcoal-100">
              {tr('No publications yet in this category. Please check back soon.')}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((p) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-2xl border border-charcoal-100 shadow-[0_8px_30px_rgba(44,30,22,0.06)] overflow-hidden flex flex-col"
                  data-testid={`publication-card-${p.id}`}
                >
                  {p.thumbnail_url && (
                    <img src={p.thumbnail_url} alt={p.title} className="w-full h-40 object-cover" />
                  )}
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="text-xs uppercase tracking-wider text-saffron-700 font-semibold mb-2">{tr(p.category)}</div>
                    <h3 className="text-lg font-heading text-charcoal-800 mb-2 leading-snug">{tr(p.title)}</h3>
                    {p.publish_date && (
                      <div className="text-xs text-charcoal-500 mb-3 flex items-center gap-1"><CalIcon size={12} /> {tr(p.publish_date)}</div>
                    )}
                    <p className="text-sm text-charcoal-600 leading-relaxed line-clamp-3 mb-4">{tr(p.abstract)}</p>
                    <div className="mt-auto flex items-center gap-3 pt-2">
                      <button
                        onClick={() => setSelected(p)}
                        className="flex-1 text-sm border border-charcoal-200 rounded-full py-2 hover:bg-cream inline-flex items-center justify-center gap-1"
                      >
                        {tr('Read More')} <ChevronRight size={14} />
                      </button>
                      {p.pdf_url && (
                        <a
                          href={p.pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 bg-saffron-600 text-white text-sm rounded-full py-2 hover:bg-saffron-500 inline-flex items-center justify-center gap-1"
                          data-testid={`publication-pdf-${p.id}`}
                        >
                          <Download size={14} /> {tr('PDF')}
                        </a>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Case Studies */}
      {caseStudies.length > 0 && (
        <section className="py-20 md:py-28 bg-white" data-testid="publications-case-studies">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <div className="text-center mb-10">
              <div className="text-saffron-600 uppercase text-xs tracking-[0.2em] font-body font-semibold mb-3">
                {tr('Featured Findings')}
              </div>
              <h2 className="text-3xl md:text-4xl font-heading text-charcoal-800">{tr('Case Studies')}</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {caseStudies.map((p) => (
                <div key={p.id} className="bg-gradient-to-br from-saffron-50 to-cream rounded-2xl p-6 border border-saffron-100">
                  <div className="text-xs uppercase tracking-wider text-saffron-700 font-semibold mb-2">{tr(p.category)}</div>
                  <h3 className="text-xl font-heading text-charcoal-800 mb-3 leading-snug">{tr(p.title)}</h3>
                  <p className="text-sm text-charcoal-600 leading-relaxed line-clamp-4">{tr(p.findings || p.abstract)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-saffron-600 to-saffron-700 text-white text-center">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <h2 className="text-3xl md:text-5xl font-heading tracking-tight mb-4">
            {t('cta_heading', 'Join Our Research Circle')}
          </h2>
          <p className="text-base md:text-lg text-white/90 leading-relaxed mb-8 max-w-2xl mx-auto">
            {t(
              'cta_description',
              'Are you a seeker, scientist, or scholar interested in Vedic sadhana? Collaborate with us on empirical studies that honour both scripture and evidence.',
            )}
          </p>
          <a
            href="/contact"
            data-testid="publications-cta"
            style={{ color: '#7a3e0d' }}
            className="inline-flex items-center gap-2 bg-white rounded-full px-8 py-3 font-body font-semibold hover:bg-cream transition-colors shadow-lg"
          >
            <Users size={20} /> <span>{tr('Participate in Research')}</span>
          </a>
        </div>
      </section>

      {/* Detail modal */}
      {selected && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-8"
            data-testid="publication-detail-modal"
          >
            <div className="text-xs uppercase tracking-wider text-saffron-700 font-semibold mb-2">{tr(selected.category)}</div>
            <h3 className="text-2xl md:text-3xl font-heading text-charcoal-800 mb-4 leading-snug">{tr(selected.title)}</h3>
            {selected.lead_guidance && (
              <p className="text-sm text-charcoal-500 italic mb-3">{tr('Lead guidance')}: {tr(selected.lead_guidance)}</p>
            )}
            {selected.team_members && selected.team_members.length > 0 && (
              <p className="text-sm text-charcoal-500 mb-3">{tr('Team')}: {selected.team_members.map((m) => tr(m)).join(', ')}</p>
            )}
            {selected.publish_date && (
              <p className="text-sm text-charcoal-500 mb-4">{tr('Published')}: {tr(selected.publish_date)}</p>
            )}
            {selected.abstract && (
              <div className="mb-4">
                <h4 className="font-heading text-charcoal-800 mb-1">{tr('Abstract')}</h4>
                <p className="text-charcoal-600 leading-relaxed whitespace-pre-line">{tr(selected.abstract)}</p>
              </div>
            )}
            {selected.scriptural_reference && (
              <div className="mb-4">
                <h4 className="font-heading text-charcoal-800 mb-1">{tr('Scriptural Reference')}</h4>
                <p className="text-charcoal-600 leading-relaxed whitespace-pre-line">{tr(selected.scriptural_reference)}</p>
              </div>
            )}
            {selected.findings && (
              <div className="mb-4">
                <h4 className="font-heading text-charcoal-800 mb-1">{tr('Findings')}</h4>
                <p className="text-charcoal-600 leading-relaxed whitespace-pre-line">{tr(selected.findings)}</p>
              </div>
            )}
            <div className="flex gap-3 pt-3">
              <button onClick={() => setSelected(null)} className="flex-1 border border-charcoal-200 rounded-full py-2.5 hover:bg-cream">{tr('Close')}</button>
              {selected.pdf_url && (
                <a href={selected.pdf_url} target="_blank" rel="noopener noreferrer" className="flex-1 bg-saffron-600 text-white rounded-full py-2.5 hover:bg-saffron-500 inline-flex items-center justify-center gap-2">
                  <FileText size={16} /> {tr('Download PDF')}
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
